import { useState, useRef } from 'react';
import { ChevronDown, ChevronUp, Plus, Trash2, GripVertical } from 'lucide-react';
import type { ProductFeatureDefinition } from '../../api/admin';

// ─── Internal draft types ───────────────────────────────────────────────────

type FieldType = 'number' | 'boolean' | 'text' | 'select';

interface SelectOption {
  value: string;
  label: string;
}

interface FeatureDraft {
  key: string;
  label: string;
  type: FieldType;
  description: string;
  adminVisible: boolean;
  // number fields
  min: string;
  max: string;
  nullable: boolean;
  nullableLabel: string;
  defaultNumber: string;
  // boolean field
  defaultBoolean: boolean;
  // text field
  defaultText: string;
  // select fields
  options: SelectOption[];
  defaultSelect: string;
}

const emptyDraft = (): FeatureDraft => ({
  key: '',
  label: '',
  type: 'boolean',
  description: '',
  adminVisible: true,
  min: '1',
  max: '100000',
  nullable: false,
  nullableLabel: 'Unlimited',
  defaultNumber: '',
  defaultBoolean: false,
  defaultText: '',
  options: [],
  defaultSelect: '',
});

// ─── Conversion helpers ──────────────────────────────────────────────────────

const schemaToDraft = (def: ProductFeatureDefinition): FeatureDraft => ({
  key: def.key,
  label: def.label,
  type: def.type,
  description: def.description || '',
  adminVisible: def.adminVisible !== false,
  min: def.min !== undefined ? String(def.min) : '1',
  max: def.max !== undefined ? String(def.max) : '100000',
  nullable: Boolean(def.nullable),
  nullableLabel: def.nullableLabel || 'Unlimited',
  defaultNumber: def.type === 'number' && def.defaultValue !== null && def.defaultValue !== undefined ? String(def.defaultValue) : '',
  defaultBoolean: def.type === 'boolean' ? Boolean(def.defaultValue) : false,
  defaultText: def.type === 'text' ? String(def.defaultValue ?? '') : '',
  options: Array.isArray(def.options) ? def.options : [],
  defaultSelect: def.type === 'select' ? String(def.defaultValue ?? '') : '',
});

const draftToSchema = (draft: FeatureDraft): ProductFeatureDefinition => {
  const base: ProductFeatureDefinition = {
    key: draft.key.trim(),
    label: draft.label.trim() || draft.key.trim(),
    type: draft.type,
    adminVisible: draft.adminVisible,
    description: draft.description.trim() || null,
  };

  if (draft.type === 'number') {
    base.min = Number(draft.min) || 1;
    base.max = Number(draft.max) || 100000;
    base.nullable = draft.nullable;
    if (draft.nullable) base.nullableLabel = draft.nullableLabel.trim() || 'Unlimited';
    base.defaultValue = draft.defaultNumber !== '' ? Number(draft.defaultNumber) : null;
  } else if (draft.type === 'boolean') {
    base.defaultValue = draft.defaultBoolean;
  } else if (draft.type === 'text') {
    base.defaultValue = draft.defaultText || null;
  } else if (draft.type === 'select') {
    base.options = draft.options.filter((o) => o.value.trim());
    base.defaultValue = draft.defaultSelect || null;
  }

  return base;
};

// ─── Sub-components ──────────────────────────────────────────────────────────

const inputCls = 'w-full h-9 px-3 bg-zinc-950 border border-zinc-700 rounded-lg text-sm text-zinc-100 focus:border-amber-500 focus:outline-none';
const labelCls = 'block text-[11px] text-zinc-500 mb-1';

const FieldRow: React.FC<{ label: string; children: React.ReactNode; half?: boolean }> = ({ label, children, half }) => (
  <div className={half ? 'flex-1 min-w-0' : ''}>
    <label className={labelCls}>{label}</label>
    {children}
  </div>
);

interface DefinitionCardProps {
  draft: FeatureDraft;
  index: number;
  isOpen: boolean;
  isDragging: boolean;
  isOver: boolean;
  onToggle: () => void;
  onChange: (updated: FeatureDraft) => void;
  onRemove: () => void;
  onDragStart: () => void;
  onDragEnter: () => void;
  onDragEnd: () => void;
}

const DefinitionCard: React.FC<DefinitionCardProps> = ({
  draft, index, isOpen, isDragging, isOver,
  onToggle, onChange, onRemove,
  onDragStart, onDragEnter, onDragEnd,
}) => {
  const set = <K extends keyof FeatureDraft>(key: K, value: FeatureDraft[K]) =>
    onChange({ ...draft, [key]: value });

  const addOption = () =>
    set('options', [...draft.options, { value: '', label: '' }]);

  const updateOption = (i: number, field: 'value' | 'label', val: string) =>
    set('options', draft.options.map((opt, idx) => idx === i ? { ...opt, [field]: val } : opt));

  const removeOption = (i: number) =>
    set('options', draft.options.filter((_, idx) => idx !== i));

  return (
    <div
      className={`rounded-xl border bg-zinc-950/60 overflow-hidden transition-all ${
        isDragging ? 'opacity-40 scale-[0.98]' : ''
      } ${isOver ? 'border-amber-500/50 shadow-[0_0_0_1px_rgba(245,158,11,0.3)]' : 'border-zinc-800'}`}
      onDragOver={(e) => { e.preventDefault(); onDragEnter(); }}
    >
      {/* Header */}
      <div
        className="flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-zinc-900/50 transition-colors"
        onClick={onToggle}
      >
        {/* Drag handle — stopPropagation so clicking it doesn't toggle the card */}
        <div
          draggable
          onDragStart={(e) => { e.stopPropagation(); onDragStart(); }}
          onDragEnd={onDragEnd}
          onClick={(e) => e.stopPropagation()}
          className="cursor-grab active:cursor-grabbing text-zinc-600 hover:text-zinc-400 transition-colors flex-shrink-0 p-0.5 -m-0.5"
          title="Drag to reorder"
        >
          <GripVertical className="w-3.5 h-3.5" />
        </div>
        <div className="flex-1 min-w-0">
          <span className="text-sm font-medium text-zinc-200 truncate">
            {draft.key || <span className="text-zinc-600 italic">untitled</span>}
          </span>
          {draft.label && draft.label !== draft.key && (
            <span className="ml-2 text-xs text-zinc-500">{draft.label}</span>
          )}
        </div>
        <span className={`text-[10px] px-2 py-0.5 rounded-full border font-medium ${
          draft.type === 'boolean' ? 'border-emerald-500/30 text-emerald-400 bg-emerald-500/10' :
          draft.type === 'number'  ? 'border-sky-500/30 text-sky-400 bg-sky-500/10' :
          draft.type === 'select'  ? 'border-purple-500/30 text-purple-400 bg-purple-500/10' :
          'border-zinc-600 text-zinc-400 bg-zinc-800'
        }`}>{draft.type}</span>
        {!draft.adminVisible && (
          <span className="text-[10px] text-zinc-600 border border-zinc-800 px-1.5 py-0.5 rounded-full">hidden</span>
        )}
        {isOpen ? <ChevronUp className="w-3.5 h-3.5 text-zinc-500 flex-shrink-0" /> : <ChevronDown className="w-3.5 h-3.5 text-zinc-500 flex-shrink-0" />}
      </div>

      {isOpen && (
        <div className="px-4 pb-4 space-y-4 border-t border-zinc-800">
          {/* Core fields */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-4">
            <FieldRow label="Key (code name)">
              <input
                value={draft.key}
                onChange={(e) => set('key', e.target.value)}
                placeholder="e.g. maxItemsPerRun"
                className={inputCls}
              />
            </FieldRow>
            <FieldRow label="Label (display name)">
              <input
                value={draft.label}
                onChange={(e) => set('label', e.target.value)}
                placeholder="e.g. Max items / run"
                className={inputCls}
              />
            </FieldRow>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <FieldRow label="Type">
              <select
                value={draft.type}
                onChange={(e) => set('type', e.target.value as FieldType)}
                className={inputCls}
              >
                <option value="boolean">boolean — on/off toggle</option>
                <option value="number">number — integer with bounds</option>
                <option value="text">text — free-form string</option>
                <option value="select">select — fixed options</option>
              </select>
            </FieldRow>
            <FieldRow label="Description (optional)">
              <input
                value={draft.description}
                onChange={(e) => set('description', e.target.value)}
                placeholder="Short hint shown to admins"
                className={inputCls}
              />
            </FieldRow>
          </div>

          {/* Type-specific fields */}
          {draft.type === 'number' && (
            <div className="space-y-3 rounded-lg border border-zinc-800 bg-zinc-900/40 p-3">
              <p className="text-[11px] text-zinc-500 font-medium uppercase tracking-wide">Number settings</p>
              <div className="flex gap-3">
                <FieldRow label="Min" half>
                  <input type="number" value={draft.min} onChange={(e) => set('min', e.target.value)} className={inputCls} />
                </FieldRow>
                <FieldRow label="Max" half>
                  <input type="number" value={draft.max} onChange={(e) => set('max', e.target.value)} className={inputCls} />
                </FieldRow>
                <FieldRow label="Default" half>
                  <input
                    type="number"
                    value={draft.defaultNumber}
                    onChange={(e) => set('defaultNumber', e.target.value)}
                    placeholder={draft.nullable ? 'null' : '—'}
                    disabled={draft.nullable && draft.defaultNumber === ''}
                    className={inputCls}
                  />
                </FieldRow>
              </div>
              <label className="flex items-center gap-2 text-sm text-zinc-300 cursor-pointer">
                <input
                  type="checkbox"
                  checked={draft.nullable}
                  onChange={(e) => set('nullable', e.target.checked)}
                  className="accent-amber-500"
                />
                Allow null (unlimited / no limit)
              </label>
              {draft.nullable && (
                <FieldRow label='Label shown when null (e.g. "Unlimited", "Full access")'>
                  <input value={draft.nullableLabel} onChange={(e) => set('nullableLabel', e.target.value)} className={inputCls} />
                </FieldRow>
              )}
            </div>
          )}

          {draft.type === 'boolean' && (
            <div className="rounded-lg border border-zinc-800 bg-zinc-900/40 p-3">
              <p className="text-[11px] text-zinc-500 font-medium uppercase tracking-wide mb-2">Boolean settings</p>
              <label className="flex items-center gap-2 text-sm text-zinc-300 cursor-pointer">
                <input
                  type="checkbox"
                  checked={draft.defaultBoolean}
                  onChange={(e) => set('defaultBoolean', e.target.checked)}
                  className="accent-amber-500"
                />
                Default value: <span className="font-medium text-zinc-200">{draft.defaultBoolean ? 'true (enabled)' : 'false (disabled)'}</span>
              </label>
            </div>
          )}

          {draft.type === 'text' && (
            <div className="rounded-lg border border-zinc-800 bg-zinc-900/40 p-3">
              <p className="text-[11px] text-zinc-500 font-medium uppercase tracking-wide mb-2">Text settings</p>
              <FieldRow label="Default value">
                <input value={draft.defaultText} onChange={(e) => set('defaultText', e.target.value)} placeholder="Leave empty for null" className={inputCls} />
              </FieldRow>
            </div>
          )}

          {draft.type === 'select' && (
            <div className="rounded-lg border border-zinc-800 bg-zinc-900/40 p-3 space-y-3">
              <p className="text-[11px] text-zinc-500 font-medium uppercase tracking-wide">Select settings</p>
              <div className="space-y-2">
                {draft.options.map((opt, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <input
                      value={opt.value}
                      onChange={(e) => updateOption(i, 'value', e.target.value)}
                      placeholder="value (code)"
                      className="h-8 flex-1 px-2 bg-zinc-950 border border-zinc-700 rounded-lg text-xs text-zinc-100"
                    />
                    <input
                      value={opt.label}
                      onChange={(e) => updateOption(i, 'label', e.target.value)}
                      placeholder="label (display)"
                      className="h-8 flex-1 px-2 bg-zinc-950 border border-zinc-700 rounded-lg text-xs text-zinc-100"
                    />
                    <button type="button" onClick={() => removeOption(i)} className="text-red-400 hover:text-red-300 p-1">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
              </div>
              <button
                type="button"
                onClick={addOption}
                className="flex items-center gap-1.5 text-xs text-amber-400 hover:text-amber-300 transition-colors"
              >
                <Plus className="w-3.5 h-3.5" /> Add option
              </button>
              {draft.options.length > 0 && (
                <FieldRow label="Default value">
                  <select
                    value={draft.defaultSelect}
                    onChange={(e) => set('defaultSelect', e.target.value)}
                    className={inputCls}
                  >
                    <option value="">— none —</option>
                    {draft.options.filter((o) => o.value.trim()).map((o) => (
                      <option key={o.value} value={o.value}>{o.label || o.value}</option>
                    ))}
                  </select>
                </FieldRow>
              )}
            </div>
          )}

          {/* Admin visibility + remove */}
          <div className="flex items-center justify-between pt-1">
            <label className="flex items-center gap-2 text-sm text-zinc-400 cursor-pointer">
              <input
                type="checkbox"
                checked={draft.adminVisible}
                onChange={(e) => set('adminVisible', e.target.checked)}
                className="accent-amber-500"
              />
              Visible in admin plan editor
            </label>
            <button
              type="button"
              onClick={onRemove}
              className="flex items-center gap-1.5 text-xs text-red-400 hover:text-red-300 transition-colors"
            >
              <Trash2 className="w-3.5 h-3.5" /> Remove field
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Main component ──────────────────────────────────────────────────────────

interface FeatureSchemaBuilderProps {
  value: ProductFeatureDefinition[];
  onChange: (schema: ProductFeatureDefinition[]) => void;
}

const FeatureSchemaBuilder: React.FC<FeatureSchemaBuilderProps> = ({ value, onChange }) => {
  const [drafts, setDrafts] = useState<FeatureDraft[]>(() => value.map(schemaToDraft));
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const dragIndex = useRef<number | null>(null);
  const [overIndex, setOverIndex] = useState<number | null>(null);

  const commit = (updated: FeatureDraft[]) => {
    setDrafts(updated);
    onChange(updated.filter((d) => d.key.trim()).map(draftToSchema));
  };

  const addField = () => {
    const next = [...drafts, emptyDraft()];
    setDrafts(next);
    setOpenIndex(next.length - 1);
  };

  const updateField = (index: number, updated: FeatureDraft) => {
    commit(drafts.map((d, i) => (i === index ? updated : d)));
  };

  const removeField = (index: number) => {
    commit(drafts.filter((_, i) => i !== index));
    setOpenIndex(null);
  };

  const handleDragStart = (index: number) => {
    dragIndex.current = index;
  };

  const handleDragEnter = (index: number) => {
    setOverIndex(index);
  };

  const handleDragEnd = () => {
    const from = dragIndex.current;
    const to   = overIndex;
    if (from !== null && to !== null && from !== to) {
      const next = [...drafts];
      const [moved] = next.splice(from, 1);
      next.splice(to, 0, moved);
      // Keep the open card tracking the item that moved
      if (openIndex === from) setOpenIndex(to);
      else if (openIndex !== null) {
        if (from < openIndex && to >= openIndex) setOpenIndex(openIndex - 1);
        else if (from > openIndex && to <= openIndex) setOpenIndex(openIndex + 1);
      }
      commit(next);
    }
    dragIndex.current = null;
    setOverIndex(null);
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs font-semibold text-zinc-300">Feature schema</p>
          <p className="text-[11px] text-zinc-500 mt-0.5">
            Defines what per-plan configuration fields exist for this product.
            Each plan you create will have values for these fields.
          </p>
        </div>
        <span className="text-xs text-zinc-600">{drafts.length} field{drafts.length !== 1 ? 's' : ''}</span>
      </div>

      {drafts.length === 0 && (
        <p className="text-xs text-zinc-600 py-3 text-center border border-dashed border-zinc-800 rounded-xl">
          No feature fields defined yet. Add one below.
        </p>
      )}

      <div
        className="space-y-2"
        onDragOver={(e) => e.preventDefault()}
        onDrop={handleDragEnd}
      >
        {drafts.map((draft, i) => (
          <DefinitionCard
            key={draft.key || `__new_${i}`}
            draft={draft}
            index={i}
            isOpen={openIndex === i}
            isDragging={dragIndex.current === i}
            isOver={overIndex === i && dragIndex.current !== i}
            onToggle={() => setOpenIndex(openIndex === i ? null : i)}
            onChange={(updated) => updateField(i, updated)}
            onRemove={() => removeField(i)}
            onDragStart={() => handleDragStart(i)}
            onDragEnter={() => handleDragEnter(i)}
            onDragEnd={handleDragEnd}
          />
        ))}
      </div>

      <button
        type="button"
        onClick={addField}
        className="flex items-center gap-2 w-full h-9 px-3 rounded-xl border border-dashed border-zinc-700 text-zinc-400 hover:border-amber-500/50 hover:text-amber-400 transition-colors text-xs justify-center"
      >
        <Plus className="w-3.5 h-3.5" /> Add feature field
      </button>
    </div>
  );
};

export default FeatureSchemaBuilder;
